import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PlanStatus } from '@testflow/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StateChip } from '../../components/atoms.js';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { mapApiError } from '../../lib/errors.js';
import {
  type OpenSignoffNode,
  type TestPlan,
  parseActivePlanConflict,
  parseOpenSignoffNodes,
  parsePlanEvidence,
  plansApi,
} from '../../lib/plans-api.js';

// #114 (A9): der Plan-Lebenszyklus in der Oberfläche — „Plan aktivieren" · „Plan abschließen" ·
// „Wieder öffnen", je nach Status genau ein Knopf. Die Regeln selbst (höchstens ein aktiver Plan,
// welche Knoten offen sind) liegen ausschließlich im Server; diese Komponente stellt sie dar und
// baut sie nicht nach.
//
// Der Abschluss hat zwei Wege, und der Client kann VORHER nicht wissen, welcher gilt: ob noch
// Bestätigungen offen sind, weiß nur der Server. Deshalb der Ablauf: kurzes Bestätigungsfenster →
// Abschluss versuchen → antwortet der Server 409 `signoffs_open`, wird daraus das Warnfenster, das
// die offenen Knoten BEIM NAMEN nennt und eine Pflichtnotiz verlangt. Ein vorgeschalteter
// Probe-Request wäre eine zweite Abfrage derselben Wahrheit und könnte zwischen Probe und
// Abschluss veralten.

// Begründungs-Dialog mit Pflichtfeld. Struktur/Escape-Handling bewusst 1:1 wie
// defects/retest-guard-dialog.tsx und components/confirm-dialog.tsx (zentriertes
// <dialog open aria-modal>, Escape in der CAPTURE-Phase mit stopPropagation) — kein neues Muster.
// Eigene Komponente statt einer weiteren Option an ConfirmDialog, weil hier zwei Dinge dazukommen,
// die dort nichts zu suchen haben: die namentliche Knotenliste und die Absende-Sperre ohne Notiz.
function PlanNoteDialog({
  title,
  body,
  openNodes,
  noteLabel,
  confirmLabel,
  pending,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  openNodes?: OpenSignoffNode[];
  noteLabel: string;
  confirmLabel: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const trimmed = note.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onCancel}
        className="absolute inset-0"
      />
      <dialog
        ref={dialogRef}
        open
        tabIndex={-1}
        aria-modal="true"
        aria-label={title}
        className="relative z-10 m-0 w-full max-w-md space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-text-body shadow-xl outline-none"
      >
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        <p className="text-sm text-text-muted">{body}</p>
        {/* Die offenen Knoten werden aufgezählt, nicht gezählt: „2 Knoten stehen offen" zwingt zum
            Nachschlagen auf einer anderen Seite, „Vertrieb, Logistik" ist sofort entscheidbar. */}
        {openNodes && openNodes.length > 0 && (
          <ul className="max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-status-warn">
            {openNodes.map((node) => (
              <li key={node.node_id}>{node.node_name}</li>
            ))}
          </ul>
        )}
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {noteLabel}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="min-h-[72px] rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
          />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('common.cancel')}
          </button>
          {/* Ohne Notiz kein Abschluss (A9) — der Knopf bleibt gesperrt, statt den Nutzer erst am
              Server scheitern zu lassen. Der Server prüft dieselbe Bedingung noch einmal. */}
          <button
            type="button"
            disabled={pending || trimmed.length === 0}
            onClick={() => onConfirm(trimmed)}
            className="rounded-btn bg-status-fail px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {confirmLabel}
          </button>
        </div>
      </dialog>
    </div>
  );
}

// #114: EIN Chip für Liste und Kopf. Getrennte Fassungen wären sofort auseinandergedriftet — die
// Kartenliste zeigte bisher nur „AKTIV" und gar nichts für alles andere, wodurch ein
// abgeschlossener Plan aussah wie ein frischer Entwurf. Der Status ist die Information, an der
// hängt, ob der Plan überhaupt noch Eingaben annimmt; er muss überall sichtbar sein.
export function PlanStatusChip({ status }: { status: PlanStatus }) {
  const { t } = useTranslation();
  return (
    <StateChip label={t(`plans.status.${status}`)} tone={status === 'aktiv' ? 'ok' : 'default'} />
  );
}

// Welches Fenster gerade offen ist. 'override' entsteht ausschließlich aus der 409-Antwort des
// Servers, nie aus einer clientseitigen Vermutung.
// 2. Abnahme, Befund 1: 'confirmReset' kommt dazu — „Aktivierung zurücknehmen". Ohne diesen Weg
// war „versehentlich aktiviert" endgültig: der Plan ließ sich nur vorwärts bewegen.
type Stage = null | 'confirmClose' | 'confirmReset' | 'override' | 'reopen';

export function PlanStatusActions({ projectId, plan }: { projectId: string; plan: TestPlan }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<Stage>(null);
  const [openNodes, setOpenNodes] = useState<OpenSignoffNode[]>([]);
  // Alles außer „noch Bestätigungen offen" (z. B. `another_plan_active`) landet als benannter
  // Text direkt neben den Knöpfen, siehe errorToast-Kommentar an der Mutation.
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['plan', projectId, plan.test_plan_id] });
    void queryClient.invalidateQueries({ queryKey: ['plans', projectId] });
  };

  // `errorToast: false` (Muster structure-panel.tsx): der 409 `signoffs_open` ist kein
  // Fehlschlag, sondern der ZWEITE WEG des Abschlusses — ein globaler roter Toast neben dem
  // Warnfenster würde behaupten, etwas sei schiefgegangen. Die übrigen Fehler verliert das
  // dadurch nicht: sie erscheinen benannt unter den Knöpfen (mapApiError, siehe onError).
  const change = useMutation({
    mutationFn: (body: { status: PlanStatus; override_note?: string; reopen_note?: string }) =>
      plansApi.setStatus(projectId, plan.test_plan_id, body),
    meta: { successToast: 'feedback.saved', errorToast: false },
    onMutate: () => setError(null),
    onSuccess: () => {
      setStage(null);
      setOpenNodes([]);
      setError(null);
      invalidate();
    },
    onError: (err) => {
      const nodes = parseOpenSignoffNodes(err);
      if (nodes) {
        setOpenNodes(nodes);
        setStage('override');
        return;
      }
      setStage(null);
      // 2. Abnahme, Befund 1: „ein anderer Plan ist aktiv" beantwortet nicht die einzige Frage,
      // die der Nutzer dann hat — WELCHER? Der Server nennt ihn jetzt, also nennt ihn auch die
      // Meldung, samt dem nächsten Schritt („dort erst die Aktivierung zurücknehmen").
      const blocking = parseActivePlanConflict(err);
      if (blocking) {
        setError(t('plans.lifecycle.blockedByPlan', { name: blocking.name }));
        return;
      }
      // Dasselbe für die Rücknahme: nicht „Fehler", sondern was im Weg steht und was stattdessen
      // zu tun ist (abschließen statt zurücksetzen).
      const evidence = parsePlanEvidence(err);
      if (evidence?.error === 'plan_reset_has_evidence') {
        setError(
          t('plans.lifecycle.resetBlocked', {
            runs: evidence.run_count,
            signoffs: evidence.signoff_count,
          }),
        );
        return;
      }
      setError(mapApiError(t, err));
    },
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {plan.status === 'entwurf' && (
          <button
            type="button"
            disabled={change.isPending}
            onClick={() => change.mutate({ status: 'aktiv' })}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('plans.lifecycle.activate')}
          </button>
        )}
        {plan.status === 'aktiv' && (
          <>
            <button
              type="button"
              disabled={change.isPending}
              onClick={() => setStage('confirmClose')}
              className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body disabled:opacity-60"
            >
              {t('plans.lifecycle.close')}
            </button>
            {/* 2. Abnahme, Befund 1: der Rückweg. Steht bewusst NEBEN „abschließen" und nicht
                versteckt — ein Nutzer mit zwei aktiven Plänen aus der Migration muss ihn ohne
                Suchen finden. Ob er erlaubt ist, entscheidet der Server (keine Läufe, keine
                Abnahmen); die Absage kommt benannt zurück. Ein clientseitiger Vorab-Check wäre
                eine zweite Rechnung derselben Wahrheit und könnte veralten. */}
            <button
              type="button"
              disabled={change.isPending}
              onClick={() => setStage('confirmReset')}
              className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body disabled:opacity-60"
            >
              {t('plans.lifecycle.reset')}
            </button>
          </>
        )}
        {plan.status === 'abgeschlossen' && (
          <button
            type="button"
            disabled={change.isPending}
            onClick={() => setStage('reopen')}
            className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body disabled:opacity-60"
          >
            {t('plans.lifecycle.reopen')}
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-sm text-status-fail">
          {error}
        </p>
      )}

      {/* Weg 1: alle Bestätigungen da → kurze Rückfrage. Führt der Versuch zu 409 signoffs_open,
          schaltet onError direkt auf das Warnfenster um (stage 'override'). */}
      {stage === 'confirmClose' && (
        <ConfirmDialog
          title={t('plans.lifecycle.closeTitle')}
          message={t('plans.lifecycle.closeBody')}
          confirmLabel={t('plans.lifecycle.close')}
          danger
          pending={change.isPending}
          onCancel={() => setStage(null)}
          onConfirm={() => change.mutate({ status: 'abgeschlossen' })}
        />
      )}

      {/* 2. Abnahme, Befund 1: Rückfrage vor der Rücknahme — sie ist folgenlos für Daten, aber
          der Plan verschwindet damit aus „Meine Tests" und aus der Sign-off-Evidenz. Keine
          Pflichtnotiz: anders als beim Wiedereröffnen wird hier kein Endzustand aufgebrochen,
          und ein Plan ohne Läufe hat nichts, was eine Begründung schützen müsste. */}
      {stage === 'confirmReset' && (
        <ConfirmDialog
          title={t('plans.lifecycle.resetTitle')}
          message={t('plans.lifecycle.resetBody')}
          confirmLabel={t('plans.lifecycle.reset')}
          pending={change.isPending}
          onCancel={() => setStage(null)}
          onConfirm={() => change.mutate({ status: 'entwurf' })}
        />
      )}

      {/* Weg 2: offene Bestätigungen → Warnfenster, das sie beim Namen nennt, plus Pflichtnotiz. */}
      {stage === 'override' && (
        <PlanNoteDialog
          title={t('plans.lifecycle.overrideTitle')}
          body={t('plans.lifecycle.overrideBody')}
          openNodes={openNodes}
          noteLabel={t('plans.lifecycle.overrideNoteLabel')}
          confirmLabel={t('plans.lifecycle.overrideConfirm')}
          pending={change.isPending}
          onCancel={() => {
            setStage(null);
            setOpenNodes([]);
            change.reset();
          }}
          onConfirm={(note) => change.mutate({ status: 'abgeschlossen', override_note: note })}
        />
      )}

      {stage === 'reopen' && (
        <PlanNoteDialog
          title={t('plans.lifecycle.reopenTitle')}
          body={t('plans.lifecycle.reopenBody')}
          noteLabel={t('plans.lifecycle.reopenNoteLabel')}
          confirmLabel={t('plans.lifecycle.reopen')}
          pending={change.isPending}
          onCancel={() => setStage(null)}
          onConfirm={(note) => change.mutate({ status: 'aktiv', reopen_note: note })}
        />
      )}
    </>
  );
}
